import { sendAnalytics } from '@audio-network/analytics'
import { useTranslation } from 'next-i18next'
import { RefObject, useEffect, useMemo } from 'react'
import { useForm, UseFormReturn, useWatch } from 'react-hook-form'

import { BulkDownloadSelection } from '@/client/downloads'

import { useDownloadContext } from '../DownloadOptionsModal.context'
import { DownloadFormValues } from '../DownloadOptionsModal.types'
import {
	getBulkDownloadItemCount,
	getBulkDownloadSelection,
	getBulkDownloadSelectionAnalytics,
	getFileFormatOptionValue,
	getFileTypeFromOptionValue,
	isDownloadFileLimitExceeded,
	shouldUseSingleTrackDownload,
} from './DownloadOptions.utils'

type DownloadOptionsModalRef = {
	close: () => void
}

export type UseDownloadOptionsFormReturn = {
	downloadOptionsFormId: string
	downloadOptionsModalHeadingId: string
	downloadTrackCount: number
	downloadTrackCountLabel: string
	formMethods: UseFormReturn<DownloadFormValues>
	handleSubmitDownload: () => void
	bulkDownloadSelection: BulkDownloadSelection
	isFileLimitExceeded: boolean
}

export const useDownloadOptionsForm = (
	modalRef: RefObject<DownloadOptionsModalRef | null>
): UseDownloadOptionsFormReturn => {
	const {
		downloadOptions,
		downloadTrack,
		downloadTracks,
		modalId,
		initialTracks,
	} = useDownloadContext()
	const { t } = useTranslation('download')
	const downloadOptionsModalHeadingId = `${modalId}-heading`
	const downloadOptionsFormId = `${modalId}-form`
	const formMethods = useForm<DownloadFormValues>({
		defaultValues: {
			fileFormat: getFileFormatOptionValue(modalId, 'wav'),
		},
	})
	const { control, handleSubmit } = formMethods
	const formValues = useWatch({ control })
	const bulkDownloadSelection = useMemo(
		() =>
			getBulkDownloadSelection(
				initialTracks,
				downloadOptions?.mixes ?? [],
				formValues,
				downloadOptions?.stems
			),
		[downloadOptions?.mixes, downloadOptions?.stems, formValues, initialTracks]
	)
	const downloadTrackCount = getBulkDownloadItemCount(bulkDownloadSelection)
	const isFileLimitExceeded = isDownloadFileLimitExceeded(downloadTrackCount)
	const downloadTrackCountLabel = t(
		downloadTrackCount === 1 ? 'download-x-track' : 'download-x-tracks',
		{ count: downloadTrackCount }
	)

	const handleDownload = (): void => {
		if (isFileLimitExceeded) {
			return
		}

		const fileType = getFileTypeFromOptionValue(
			modalId,
			String(formValues.fileFormat ?? '')
		)

		if (shouldUseSingleTrackDownload(bulkDownloadSelection, formValues)) {
			void downloadTrack(bulkDownloadSelection.tracks[0], fileType)
		} else {
			void downloadTracks(bulkDownloadSelection, fileType, {
				selectionAnalytics: getBulkDownloadSelectionAnalytics(
					downloadOptions?.mixes ?? [],
					formValues,
					initialTracks
				),
			})
		}

		modalRef.current?.close()
	}

	return {
		downloadOptionsFormId,
		downloadOptionsModalHeadingId,
		downloadTrackCount,
		downloadTrackCountLabel,
		formMethods,
		handleSubmitDownload: handleSubmit(handleDownload),
		bulkDownloadSelection,
		isFileLimitExceeded,
	}
}

export const useFileLimitViewedAnalytics = (
	isFileLimitExceeded: boolean,
	downloadTrackCount: number
): void => {
	useEffect(() => {
		if (!isFileLimitExceeded) {
			return
		}

		sendAnalytics({
			noun: 'Download File Limit',
			pastTenseVerb: 'Viewed',
			eventParameters: {
				FileCount: downloadTrackCount,
			},
		})
		// Fire when the alert appears, not when FileCount changes while it stays shown
		// eslint-disable-next-line react-hooks/exhaustive-deps -- downloadTrackCount captured on appear only
	}, [isFileLimitExceeded])
}
